/**
 * useCelebration - Reusable celebration effects hook
 *
 * Provides consistent celebration animations across the app:
 * - Round win confetti
 * - Game win celebration
 * - Achievement unlocks
 * - Custom celebrations
 */

import { useCallback } from "react";
import confetti from "canvas-confetti";

type CelebrationType = "roundWin" | "gameWin" | "achievement" | "subtle";

interface CelebrationOptions {
  /** Where confetti originates (0-1, defaults vary by type) */
  origin?: { x: number; y: number };
  /** Custom colors for confetti */
  colors?: string[];
  /** Number of particles (defaults vary by type) */
  particleCount?: number;
}

// Standard color palettes for different celebration types
const CELEBRATION_COLORS = {
  default: ["#a855f7", "#ec4899", "#f97316", "#fbbf24", "#22c55e"],
  gold: ["#fbbf24", "#f59e0b", "#fcd34d", "#fef3c7"],
  rainbow: ["#ef4444", "#f97316", "#fbbf24", "#22c55e", "#3b82f6", "#8b5cf6"],
};

/**
 * Hook for triggering celebration effects
 *
 * @example
 * const { celebrate, celebrateRoundWin, celebrateGameWin } = useCelebration();
 *
 * // On round win
 * celebrateRoundWin();
 *
 * // On game win (more dramatic)
 * celebrateGameWin();
 *
 * // Custom celebration
 * celebrate("achievement", { colors: ["#ffd700"] });
 */
export function useCelebration() {
  /**
   * Celebrate a round win - moderate confetti burst
   */
  const celebrateRoundWin = useCallback((options?: CelebrationOptions) => {
    const colors = options?.colors ?? CELEBRATION_COLORS.default;
    const origin = options?.origin ?? { x: 0.5, y: 0.6 };
    const particleCount = options?.particleCount ?? 80;

    confetti({
      particleCount,
      spread: 60,
      origin,
      colors,
      ticks: 200,
    });
  }, []);

  /**
   * Celebrate a game win - dramatic multi-burst celebration
   */
  const celebrateGameWin = useCallback((options?: CelebrationOptions) => {
    const colors = options?.colors ?? CELEBRATION_COLORS.rainbow;

    // Left burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { x: 0.1, y: 0.6 },
      colors,
      ticks: 300,
    });

    // Right burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { x: 0.9, y: 0.6 },
      colors,
      ticks: 300,
    });

    // Center burst (delayed)
    setTimeout(() => {
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { x: 0.5, y: 0.5 },
        colors: [...colors, "#ffffff"],
        ticks: 400,
      });
    }, 200);

    // Extra shower from top (delayed more)
    setTimeout(() => {
      confetti({
        particleCount: 50,
        spread: 180,
        startVelocity: 25,
        decay: 0.92,
        origin: { x: 0.5, y: 0 },
        colors,
      });
    }, 500);
  }, []);

  /**
   * Celebrate an achievement unlock - gold-themed burst
   */
  const celebrateAchievement = useCallback((options?: CelebrationOptions) => {
    const colors = options?.colors ?? CELEBRATION_COLORS.gold;
    const origin = options?.origin ?? { x: 0.5, y: 0.5 };

    confetti({
      particleCount: 60,
      spread: 45,
      origin,
      colors,
      shapes: ["star", "circle"],
      ticks: 150,
    });
  }, []);

  /**
   * Subtle celebration - small burst for minor victories
   */
  const celebrateSubtle = useCallback((options?: CelebrationOptions) => {
    const colors = options?.colors ?? CELEBRATION_COLORS.default;
    const origin = options?.origin ?? { x: 0.5, y: 0.7 };

    confetti({
      particleCount: 30,
      spread: 30,
      origin,
      colors,
      ticks: 100,
      gravity: 1.2,
    });
  }, []);

  /**
   * Generic celebrate function - choose celebration type
   */
  const celebrate = useCallback(
    (type: CelebrationType = "roundWin", options?: CelebrationOptions) => {
      switch (type) {
        case "gameWin":
          celebrateGameWin(options);
          break;
        case "achievement":
          celebrateAchievement(options);
          break;
        case "subtle":
          celebrateSubtle(options);
          break;
        case "roundWin":
        default:
          celebrateRoundWin(options);
          break;
      }
    },
    [celebrateRoundWin, celebrateGameWin, celebrateAchievement, celebrateSubtle]
  );

  /**
   * Fireworks effect - for very special occasions
   */
  const fireworks = useCallback(() => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: CELEBRATION_COLORS.rainbow,
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: CELEBRATION_COLORS.rainbow,
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  return {
    celebrate,
    celebrateRoundWin,
    celebrateGameWin,
    celebrateAchievement,
    celebrateSubtle,
    fireworks,
    colors: CELEBRATION_COLORS,
  };
}

export type { CelebrationType, CelebrationOptions };
